import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Col, Row, Table, Spinner, Card, Badge, Container } from 'react-bootstrap';
import axios from 'axios';
import { authHeader } from '../../../services/auth-header';
import { BASE_URL } from '../../../services/Api';
import { FaArrowLeft } from 'react-icons/fa';

function PIView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        enquiryno: '',
        enquirydate: '',
        status: '',
        supplier: null,
        contactperson: '',
        contactno: '',
        email: '',
        supplierMultipleAddress: [],
        customermaterialcode: '',
        currency: '',
        purchaseEnquiryItems: []
    });
    const [enquiryData, setEnquiryData] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${BASE_URL}purchaseEnquiry/findById/${id}`, {
                    headers: authHeader()
                });

                setEnquiryData(res.data);
                const enquiry = res.data;

                setData({
                    enquiryno: enquiry.enquiryno,
                    enquirydate: enquiry.enquirydate,
                    status: enquiry.status,
                    supplier: {
                        value: enquiry.supplier.id,
                        label: enquiry.supplier.companyname
                    },
                    contactperson: enquiry.contactperson,
                    contactno: enquiry.contactno,
                    email: enquiry.email,
                    supplierMultipleAddress: enquiry.supplierMultipleAddress ? [{
                        value: enquiry.supplierMultipleAddress.id,
                        label: enquiry.supplierMultipleAddress.supplieraddress
                    }] : [],
                    customermaterialcode: enquiry.customermaterialcode,
                    currency: enquiry.purchaseEnquiryItems.length > 0 ? enquiry.purchaseEnquiryItems[0].currency : '',
                    purchaseEnquiryItems: enquiry.purchaseEnquiryItems.map(item => ({
                        ...item,
                        label: item.product.description
                    }))
                });
            } catch (err) {
                setError('Failed to fetch enquiry details. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Draft': return 'secondary';
            case 'Submitted': return 'primary';
            case 'Approved': return 'success';
            case 'Rejected': return 'danger';
            default: return 'info';
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger text-center mt-4">{error}</div>
        );
    }

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">INTERNATIONAL PURCHASE ENQUIRY - VIEW</h5>
                        <button
                            className="btn  btn-sm me-2"
                            onClick={() => navigate(`/piduplicate/${enquiryData.id}`)}
                        >
                            📋 Duplicate Entry
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-1" />
                            Back
                        </button>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Enquiry No</small>
                                <div className="fw-bold">{data.enquiryno}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Date</small>
                                <div className="fw-bold">{formatDate(data.enquirydate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Currency</small>
                                <div className="fw-bold">{data.currency || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge
                                        bg={getStatusBadge(data.status)}
                                        className="px-2 py-1"
                                    >
                                        {data.status}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Supplier Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Supplier Details</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Company Name</small>
                                <div className="fw-bold">{data.supplier?.label || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Contact Person</small>
                                <div className="fw-bold">{data.contactperson || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Phone</small>
                                <div className="fw-bold">{data.contactno || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Email</small>
                                <div className="fw-bold">{data.email || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Customer Material Code</small>
                                <div className="fw-bold">{data.customermaterialcode || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Address</small>
                                <div className="fw-bold">
                                    {data.supplierMultipleAddress?.[0]?.label || '-'}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Enquiry Items */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Enquiry Items</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Product Description</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">Model No</th>
                                    <th className="fw-bold py-2">Tag No</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">Product Code</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-center">Currency</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.purchaseEnquiryItems.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{item.label}</div>
                                            {item.productremarks && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {item.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{item.serialnumber || '-'}</td>
                                        <td>{item.modelnumber || '-'}</td>
                                        <td>{item.tagnumber || '-'}</td>
                                        <td>{item.partno || '-'}</td>
                                        <td>{item.moc || '-'}</td>
                                        <td>{item.hsnsacno || '-'}</td>
                                        <td>{item.itemcode || '-'}</td>
                                        <td className="text-center">{item.quantity || '0'}</td>
                                        <td className="text-center">{item.uom || '-'}</td>
                                        <td className="text-center fw-bold">{item.currency || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </button>
            </div>
        </Container>
    );
}

export default PIView;


